import { Box, Button, Stack, styled } from "@mui/material";
import { customColors } from "models/customColors";
import { AuthContainer, AuthFormContent } from "SC";
import { Footer } from "./Footer";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as Logo } from "images/rs-logo.svg";
import { ModalSpinner } from "./ModalSpinner";
import { GlobalContext } from "store/globalContext";
import { CommonOutletTemplate } from "UI/CommonOutletTemplate";

const Title = styled(Box)`
  color: ${customColors.black};
  font-size: 32px;
  font-family: "Inter Bold";
  word-wrap: break-word;
`;

const Info = styled(Box)`
  color: ${customColors.black};
  font-size: 18px;
  font-family: Inter;
  font-weight: 400;
  word-wrap: break-word;
  text-align: left;
`;

const GrayButton = styled(Button)`
  padding: 4px 12px;
  color: #225da4;
  background-color: #ccd4dd;
  border-radius: 4px;

  &:hover {
    background-color: #d4dce6;
  }
`;

const NavButtons = ({ navigateToLogin }) => (
  <div className="flex flex-row gap-2 justify-center items-center">
    <GrayButton size="small" onClick={navigateToLogin}>
      Log In as Recipient
    </GrayButton>
    <GrayButton
      size="small"
      onClick={() => {
        window.location.href =
          process.env.REACT_APP_CURRENT_ENV === "dev"
            ? "https://button-dev.referral-stream.com"
            : "https://button.referral-stream.com";
      }}
    >
      Log In as a Sender
    </GrayButton>
  </div>
);

const ConditionalWrappers = ({
  condition,
  wrapperFalse: WrapperFalse,
  wrapperTrue: WrapperTrue,
  children,
}) => {
  if (condition) {
    return <WrapperTrue>{children}</WrapperTrue>;
  }
  return <WrapperFalse>{children}</WrapperFalse>;
};

export const UnsubscribePage = () => {
  const [phone, setPhone] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { token } = useParams();
  const { userData } = useContext(GlobalContext);
  const isLoggedOut = !userData?.cognitoId;
  const [state, setState] = useState(null);

  const unsubscribeByToken = async () => {
    setIsLoaded(false);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/check_unsubscribe_token`,
        {
          method: "POST",
          body: JSON.stringify({
            token,
            action: "unsubscribe",
          }),
        }
      );
      const result = await response.json();
      if (result?.error) {
        setError(result?.error ?? "Something went wrong");
        setIsLoaded(true);
        return;
      }
      if (result?.isExpired) {
        setIsExpired(true);
        setIsLoaded(true);
        return;
      }

      if (result?.state === "unsubscribed") {
        setState(result?.state);
        setPhone(result?.phone);
        return;
      }

      throw new Error("Something went wrong");
    } catch (error) {
      setError(error?.message ?? "Something went wrong");
      setIsLoaded(true);
    } finally {
      setIsLoaded(true);
    }
  };

  const checkToken = async (token) => {
    if (!token) {
      setError("Your unsubscribe link is expired.");
      setIsLoaded(true);
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/check_unsubscribe_token`,
        {
          method: "POST",
          body: JSON.stringify({
            token,
          }),
        }
      );
      const result = await response.json();
      if (result?.error) {
        setError(result?.error ?? "Something went wrong");
        setIsLoaded(true);
        return;
      }
      if (result?.isExpired) {
        setIsExpired(true);
        setIsLoaded(true);
        return;
      }
      setPhone(result?.phone);
      setState(result?.state);
      setIsLoaded(true);
    } catch (error) {
      setError(error?.message ?? "Something went wrong");
      setIsLoaded(true);
    }
  };

  useEffect(() => {
    checkToken(token);
  }, [token]);

  if (error) {
    return (
      <ConditionalWrappers
        condition={isLoggedOut}
        wrapperTrue={AuthContainer}
        wrapperFalse={CommonOutletTemplate}
      >
        <Stack
          flex={1}
          height={"90vh"}
          gap={5}
          justifyContent={"flex-start"}
          alignItems={"center"}
          mt={6}
        >
          {isLoggedOut && <Logo />}
          <div className="flex flex-col justify-center items-center gap-4">
            <AuthFormContent gap={3}>
              <Title>Unsubscription Failed</Title>
              <Stack direction={"row"} justifyContent={"flex-start"} gap={1}>
                <Info>{error}</Info>
              </Stack>
            </AuthFormContent>
            {isLoggedOut && (
              <NavButtons navigateToLogin={() => navigate("/")} />
            )}
          </div>
        </Stack>
        <Box sx={{ height: "10vh" }}></Box>
        {isLoggedOut && <Footer />}
      </ConditionalWrappers>
    );
  }

  if (!isLoaded) {
    return (
      <ConditionalWrappers
        condition={isLoggedOut}
        wrapperTrue={AuthContainer}
        wrapperFalse={CommonOutletTemplate}
      >
        <Stack
          flex={1}
          height={"90vh"}
          gap={5}
          justifyContent={"flex-start"}
          alignItems={"center"}
          mt={6}
        >
          {isLoggedOut && <Logo />}
          <ModalSpinner open={true} />
        </Stack>
        <Box sx={{ height: "10vh" }}></Box>
        {isLoggedOut && <Footer />}
      </ConditionalWrappers>
    );
  }

  if (state === "subscribed") {
    return (
      <ConditionalWrappers
        condition={isLoggedOut}
        wrapperTrue={AuthContainer}
        wrapperFalse={CommonOutletTemplate}
      >
        <Stack
          flex={1}
          height={"90vh"}
          gap={5}
          justifyContent={"flex-start"}
          alignItems={"center"}
          mt={6}
        >
          {isLoggedOut && <Logo />}
          <div className="flex flex-col justify-center items-center gap-4">
            <AuthFormContent gap={3}>
              <Title>Confirm Unsubscription</Title>
              <Stack direction={"row"} justifyContent={"center"} gap={1}>
                <Info>
                  Are you sure you want to unsubscribe the phone number{" "}
                  <b>{phone}</b> from Referral Stream SMS notifications?
                </Info>
              </Stack>
              <Stack direction={"row"} justifyContent={"center"} gap={1}>
                <Button variant="outlined" onClick={() => navigate("/")}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={unsubscribeByToken}>
                  Unsubscribe
                </Button>
              </Stack>
            </AuthFormContent>
            {isLoggedOut && (
              <NavButtons navigateToLogin={() => navigate("/")} />
            )}
          </div>
        </Stack>
        <Box sx={{ height: "10vh" }}></Box>
        {isLoggedOut && <Footer />}
      </ConditionalWrappers>
    );
  }

  return (
    <ConditionalWrappers
      condition={isLoggedOut}
      wrapperTrue={AuthContainer}
      wrapperFalse={CommonOutletTemplate}
    >
      <Stack
        flex={1}
        height={"90vh"}
        gap={5}
        justifyContent={"flex-start"}
        alignItems={"center"}
        mt={6}
      >
        {isLoggedOut && <Logo />}
        <div className="flex flex-col justify-center items-center gap-4">
          <AuthFormContent gap={3}>
            {isExpired ? (
              <>
                <Title>Link Expired</Title>
                <Stack direction={"row"} justifyContent={"center"} gap={1}>
                  <Info>
                    The link you used to unsubscribe has expired. Please contact
                    our support team at{" "}
                    <a href="mailto:support@referral-stream.com">
                      support@referral-stream.com
                    </a>
                    .
                  </Info>
                </Stack>
              </>
            ) : (
              <>
                <Title>You've Been Unsubscribed</Title>
                <Stack direction={"row"} justifyContent={"center"} gap={1}>
                  <Info>
                    You have successfully unsubscribed from our SMS
                    notifications. Your phone number, <b>{phone}</b>, has been
                    removed from our list, and you will no longer receive
                    messages from Referral Stream.
                  </Info>
                </Stack>
              </>
            )}
          </AuthFormContent>
          {isLoggedOut && <NavButtons navigateToLogin={() => navigate("/")} />}
        </div>
      </Stack>
      <Box sx={{ height: "10vh" }}></Box>
      {isLoggedOut && <Footer />}
    </ConditionalWrappers>
  );
};
